import { api } from './api.ts';
import { fromBase64, toBase64 } from './crypto/primitives.ts';
import { generateKeyPair, importPrivateKey, unwrapBytes, wrapBytesFor, type KeyAlgorithm } from './crypto/keypair.ts';
import { requireVault, type UnlockedOrganisation } from './crypto/vault.ts';

/**
 * Safes aus Kundensicht (Karsten, 16.10.2026): „Mein Safe", „Familie Fischer",
 * „Tamaras Safe". Technisch Organisationen (siehe backend/src/routes/safes.ts) –
 * hier wird daraus das, was ein Mensch ohne Computerkenntnisse versteht:
 * Schränke mit Farbe, Symbol und Namen, und Schlüssel dazu.
 *
 * Anders als beim alten Organisations-Umschalter sind ALLE Safes einer Sitzung
 * gleichzeitig aufgeschlossen. Der Safe ist nur noch die Antwort auf „Wo suchen?"
 * und „Wohin legen?".
 */

export interface SafeSchluesselMaterial {
  algorithm: KeyAlgorithm;
  publicKey: string;
  wrappedPrivateKey: string | null;
}

export interface Safe {
  kundeId: string;
  /** Anzeigename aus der Verwaltung – bei privaten Safes der Name des Menschen. */
  name: string | null;
  typ: 'privat' | 'familie' | 'verein' | 'gewerbe' | 'organisation' | string;
  zugang: 'mitglied' | 'gast';
  rolle: string | null;
  umfang?: 'alles' | 'faecher';
  darfAblegen: boolean;
  darfLoeschen: boolean;
  /** Gehört der Safe meinem eigenen Kundenkonto (Kontingent, Protokoll)? */
  eigenesKonto: boolean;
  schluessel: SafeSchluesselMaterial | null;
}

/** Was die Oberfläche über einen Safe wissen muss – mit sprechendem Namen, Farbe und Zeichen. */
export interface SafeAnzeige extends Safe {
  /** Wie der Safe dem Nutzer heißt: „Mein Safe", „Familie Fischer", „Tamaras Safe". */
  titel: string;
  /** Ein Buchstabe fürs Türschild. */
  zeichen: string;
  /** CSS-Farbwert. */
  farbe: string;
  /** Mitglied, aber Schlüssel fehlt noch (jemand anderes muss ihn geben). */
  gesperrt: boolean;
}

let geladen: SafeAnzeige[] = [];
const schluessel = new Map<string, UnlockedOrganisation>();

/** Die Farben der Safes – fest je Art, damit „Mein Safe" immer gleich aussieht. */
const FARBEN = {
  mein: '#2a7f62',
  familie: '#c0672a',
  organisation: '#1f4e79',
  gast: ['#7a4ea3', '#2f6fb3', '#a3503f', '#4f7a2a', '#8a6d1f'],
};

/** „Tamara Fischer" → „Tamaras Safe", „Marius Fischer" → „Marius' Safe". */
export function fremderSafeName(name: string | null): string {
  const vorname = (name ?? '').trim().split(/\s+/)[0] ?? '';
  if (!vorname) return 'Fremder Safe';
  return /[sßxz]$/i.test(vorname) ? `${vorname}' Safe` : `${vorname}s Safe`;
}

function anzeige(safe: Safe, gastIndex: number): SafeAnzeige {
  const istMein = safe.zugang === 'mitglied' && safe.typ === 'privat';
  const istFamilie = safe.zugang === 'mitglied' && safe.typ === 'familie';
  const titel = istMein
    ? 'Mein Safe'
    : safe.zugang === 'gast'
      ? fremderSafeName(safe.name)
      : (safe.name ?? 'Gemeinsamer Safe');
  const zeichen = istMein ? 'M' : (safe.zugang === 'gast' ? (safe.name ?? 'S') : titel).replace(/^Familie\s+/i, 'F ').trim().charAt(0).toUpperCase() || 'S';
  const farbe = istMein
    ? FARBEN.mein
    : istFamilie
      ? FARBEN.familie
      : safe.zugang === 'gast'
        ? FARBEN.gast[gastIndex % FARBEN.gast.length]!
        : FARBEN.organisation;
  const gesperrt = safe.zugang === 'mitglied' && !schluessel.has(safe.kundeId);
  return { ...safe, titel, zeichen, farbe, gesperrt };
}

/** Alle Safes der Sitzung, in Anzeigereihenfolge: Mein Safe, Familie, weitere eigene, fremde. */
export function alleSafes(): SafeAnzeige[] {
  return geladen;
}

export function safeVon(kundeId: string | null | undefined): SafeAnzeige | undefined {
  if (!kundeId) return geladen.find((s) => s.zugang === 'mitglied' && s.typ === 'privat');
  return geladen.find((s) => s.kundeId === kundeId);
}

export function meinSafe(): SafeAnzeige | undefined {
  return geladen.find((s) => s.zugang === 'mitglied' && s.typ === 'privat');
}

/** Safes, in die man ablegen darf – eigene immer, fremde nur mit „darf auch ablegen". */
export function ablegbareSafes(): SafeAnzeige[] {
  return geladen.filter((s) => s.darfAblegen && !s.gesperrt);
}

/** Der entsperrte Schlüssel eines Safes (nur Mitglieder halten ihn). */
export function safeSchluessel(kundeId: string): UnlockedOrganisation | null {
  return schluessel.get(kundeId) ?? null;
}

export function safesVerwerfen(): void {
  geladen = [];
  schluessel.clear();
}

/**
 * Lädt alle Safes und schließt auf, was sich aufschließen lässt. Für einen eigenen
 * Safe ohne jeden Schlüssel (frisch von der Verwaltung angelegt) legt das erste
 * Mitglied ihn an – wie bisher beim Organisationswechsel. Ein Safe, dessen Schlüssel
 * existiert, aber noch nicht für mich verpackt ist, bleibt zu, bis ein Mitglied ihn
 * mir gibt (`schluesselVerteilen`, läuft bei dem automatisch beim Anmelden).
 */
export async function ladeSafes(): Promise<SafeAnzeige[]> {
  const vault = requireVault();
  const { safes } = await api.get<{ safes: Safe[] }>('/api/safes');
  schluessel.clear();

  for (const safe of safes) {
    if (safe.zugang !== 'mitglied') continue;
    try {
      if (safe.schluessel?.wrappedPrivateKey) {
        const pkcs8 = await unwrapBytes(fromBase64(safe.schluessel.wrappedPrivateKey), vault.privateKey, vault.algorithm);
        schluessel.set(safe.kundeId, {
          kundeId: safe.kundeId,
          algorithm: safe.schluessel.algorithm,
          privateKey: await importPrivateKey(pkcs8, safe.schluessel.algorithm),
          publicKeyRaw: fromBase64(safe.schluessel.publicKey),
          privateKeyPkcs8: pkcs8,
        });
      } else if (!safe.schluessel) {
        // Noch gar kein Schlüssel: als (vermutlich) erstes Mitglied anlegen. Dafür
        // muss der Safe die gewählte Organisation der Sitzung sein (Server-Route).
        await api.post('/api/organisationen/wechseln', { kundeId: safe.kundeId });
        const neu = await generateKeyPair(vault.algorithm);
        const wrappedForSelf = await wrapBytesFor(neu.privateKeyPkcs8, vault.publicKeyRaw, vault.algorithm);
        await api.post('/api/keys/organisation/setup', {
          algorithm: neu.algorithm,
          publicKey: toBase64(neu.publicKeyRaw),
          wrappedPrivateKeyForSelf: toBase64(wrappedForSelf),
        });
        safe.schluessel = { algorithm: neu.algorithm, publicKey: toBase64(neu.publicKeyRaw), wrappedPrivateKey: toBase64(wrappedForSelf) };
        schluessel.set(safe.kundeId, {
          kundeId: safe.kundeId,
          algorithm: neu.algorithm,
          privateKey: await importPrivateKey(neu.privateKeyPkcs8, neu.algorithm),
          publicKeyRaw: neu.publicKeyRaw,
          privateKeyPkcs8: neu.privateKeyPkcs8,
        });
      }
    } catch (fehler) {
      // Ein Safe, der sich nicht aufschließen lässt, blockiert nicht die anderen.
      console.warn('Safe konnte nicht aufgeschlossen werden:', safe.kundeId, fehler);
    }
  }

  // Reihenfolge: Mein Safe, Familie, weitere eigene, dann fremde (Gast).
  const rang = (s: Safe): number =>
    s.zugang === 'mitglied' ? (s.typ === 'privat' ? 0 : s.typ === 'familie' ? 1 : 2) : 3;
  let gastIndex = 0;
  geladen = [...safes]
    .sort((a, b) => rang(a) - rang(b))
    .map((s) => anzeige(s, s.zugang === 'gast' ? gastIndex++ : 0));

  // Schlüssel an Familienmitglieder weitergeben, die ihn noch nicht haben – still,
  // ohne Klick: Sobald Tamara sich einmal angemeldet hat, bekommt sie ihn beim
  // nächsten Anmelden von Karsten. Die Freigaben-Ansicht zeigt den Stand sichtbar an.
  void schluesselVerteilen().catch(() => undefined);
  return geladen;
}

export interface Mitglied {
  personId: string;
  rolle: string;
  name: string;
  ich: boolean;
  bereit: boolean;
  hatSchluessel: boolean;
  algorithm: KeyAlgorithm | null;
  publicKey: string | null;
}

export async function mitgliederVon(kundeId: string): Promise<Mitglied[]> {
  const { mitglieder } = await api.get<{ mitglieder: Mitglied[] }>(`/api/safes/${kundeId}/mitglieder`);
  return mitglieder;
}

/**
 * Für jeden gemeinsamen Safe, dessen Schlüssel ich halte: den Schlüssel für alle
 * Mitglieder verpacken, die schon einen Personenschlüssel haben, aber noch keinen
 * Safe-Schlüssel. Liefert, wie viele Schlüssel verteilt wurden.
 */
export async function schluesselVerteilen(): Promise<number> {
  let verteilt = 0;
  for (const safe of geladen) {
    if (safe.zugang !== 'mitglied' || safe.typ === 'privat') continue;
    const org = schluessel.get(safe.kundeId);
    if (!org) continue;
    const mitglieder = await mitgliederVon(safe.kundeId);
    const offen = mitglieder.filter((m) => !m.ich && m.bereit && !m.hatSchluessel && m.publicKey && m.algorithm);
    if (offen.length === 0) continue;
    const wraps = [];
    for (const m of offen) {
      wraps.push({
        personId: m.personId,
        wrappedPrivateKey: toBase64(await wrapBytesFor(org.privateKeyPkcs8, fromBase64(m.publicKey!), m.algorithm!)),
      });
    }
    const r = await api.post<{ verteilt: number }>(`/api/safes/${safe.kundeId}/schluessel`, { wraps });
    verteilt += r.verteilt;
  }
  return verteilt;
}

// ---------------------------------------------------------------------------
// Gäste: „Wer darf in meinen Safe schauen?"
// ---------------------------------------------------------------------------

export interface Person {
  personId: string;
  name: string;
  email: string | null;
  /** Über welchen gemeinsamen Safe man sich kennt („Familie Fischer"). */
  gemeinsam: string | null;
  bereit: boolean;
  /** Per E-Mail eingeladen (Gastkonto), kein gemeinsamer Safe. */
  eingeladen?: boolean;
  algorithm: KeyAlgorithm | null;
  publicKey: string | null;
  gast: { umfang: 'alles' | 'faecher'; darfAblegen: boolean; faecher: string[]; ausstehend?: boolean } | null;
}

export async function personen(): Promise<{ meinSafe: string | null; personen: Person[] }> {
  return api.get('/api/safes/personen');
}

export interface Gast {
  personId: string;
  name: string;
  umfang: 'alles' | 'faecher';
  darfAblegen: boolean;
  faecher: string[];
  algorithm: KeyAlgorithm | null;
  publicKey: string | null;
  /** Absicht eingetragen, Umschläge fehlen noch (Gast hatte noch keinen Schlüssel). */
  ausstehend?: boolean;
  /** Dokumente des Safes ohne Umschlag für diesen Gast (bei „Fächer" ohne Rücksicht auf die Fächer gezählt). */
  fehlend?: number;
}

export interface EinladungsErgebnis {
  personId: string;
  email: string;
  name: string;
  bereit: boolean;
  neu: boolean;
  publicKey: string | null;
  algorithm: KeyAlgorithm | null;
  safeAngelegt?: boolean;
}

/** Jemand anderem (auch ohne Konto) einen Schlüssel zum eigenen Safe geben – die Absicht. */
export async function gastEinladen(
  meinKundeId: string,
  eingabe: { email: string; name?: string; umfang: 'alles' | 'faecher'; darfAblegen: boolean; folderIds: string[] },
): Promise<EinladungsErgebnis> {
  return api.post(`/api/safes/${meinKundeId}/gaeste/einladen`, eingabe);
}

/** Jemanden in einen gemeinsamen Safe aufnehmen (Sitzprüfung in der Verwaltung). */
export async function mitgliedAufnehmen(kundeId: string, email: string, name?: string): Promise<EinladungsErgebnis> {
  return api.post(`/api/safes/${kundeId}/mitglieder`, { email, name });
}

export async function gaesteVon(kundeId: string): Promise<Gast[]> {
  const { gaeste } = await api.get<{ gaeste: Gast[] }>(`/api/safes/${kundeId}/gaeste`);
  return gaeste;
}

/**
 * Umschläge nachreichen: Gäste meines Safes, die ihren Schlüssel inzwischen haben
 * (erste Anmeldung erledigt), aber noch keine Umschläge – für jeden werden die
 * Dokumentschlüssel gemäß Umfang verpackt. Braucht die entschlüsselten Dokumente,
 * läuft deshalb nach `sorgeFuerEntschluesselte`. Liefert die Namen der Versorgten.
 */
export async function gaesteNachreichen(
  dokumente: Array<{ id: string; dek: CryptoKey; folderId: string | null; safeKundeId: string | null }>,
  ordnerMitUnterordnern: (ids: string[]) => Set<string>,
): Promise<string[]> {
  const mein = meinSafe();
  if (!mein) return [];
  const gaeste = await gaesteVon(mein.kundeId).catch(() => []);
  // Ausstehend (erste Umschläge) oder lückenhaft (Dokumente, die andere abgelegt haben):
  // in beiden Fällen den vollständigen Satz neu verpacken – der Server ersetzt ihn.
  const offen = gaeste.filter((g) => (g.ausstehend || (g.fehlend ?? 0) > 0) && g.publicKey && g.algorithm);
  const versorgt: string[] = [];
  const { wrapDekFor } = await import('./crypto/keypair.ts');
  for (const g of offen) {
    let meine = dokumente.filter((d) => (d.safeKundeId ?? mein.kundeId) === mein.kundeId);
    if (g.umfang === 'faecher') {
      const menge = ordnerMitUnterordnern(g.faecher);
      meine = meine.filter((d) => d.folderId && menge.has(d.folderId));
    }
    const pub = fromBase64(g.publicKey!);
    const wraps = [];
    for (const d of meine) wraps.push({ documentId: d.id, wrappedDek: toBase64(await wrapDekFor(d.dek, pub, g.algorithm!)) });
    await api.post(`/api/safes/${mein.kundeId}/gaeste`, { personId: g.personId, umfang: g.umfang, darfAblegen: g.darfAblegen, folderIds: g.faecher, wraps });
    versorgt.push(g.name);
  }
  return versorgt;
}

/** Vorname für Sätze wie „Tamara kann jetzt …". */
export function vorname(name: string): string {
  return name.trim().split(/\s+/)[0] || name;
}
